import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import SendIcon from "@mui/icons-material/Send";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { TestId } from "../constants/testIds";
import DetailHeaderSplitActions, {
  type DetailHeaderAction,
} from "../components/common/DetailHeaderSplitActions";
import DetailPageHeader from "../components/common/DetailPageHeader";
import StatusWorkflowStepper from "../components/common/StatusWorkflowStepper";
import ReportRequestRevisionDialog from "../components/uangJalanReports/ReportRequestRevisionDialog";
import ReportSummaryCards from "../components/uangJalanReports/ReportSummaryCards";
import UangJalanReportHistorySection from "../components/uangJalanReports/UangJalanReportHistorySection";
import UangJalanReportOrdersSection from "../components/uangJalanReports/UangJalanReportOrdersSection";
import UangJalanReportStandbySection from "../components/uangJalanReports/UangJalanReportStandbySection";
import {
  approveUangJalanReportApi,
  downloadUangJalanReportExcelApi,
  getUangJalanReportByIdApi,
  normalizeBlobApiError,
  submitUangJalanReportForReviewApi,
  verifyUangJalanReportApi,
} from "../api/uangJalanReportsApi";
import { isOrderApproved, OrderStatus, type Order } from "../types/order";
import type { StandbyOrder } from "../types/standbyOrder";
import {
  getReportClaimMembers,
  isReportApproved,
  canDownloadUangJalanReportExcel,
  UangJalanReportStatus,
  type UangJalanReport,
} from "../types/uangJalanReport";
import {
  canMutateOrders,
  getClientTimezone,
  isFinance,
  isFinanceApprover,
  isUserAdmin,
} from "../types/user";
import { getErrorMessage } from "../utils/errorHandling";
import { getReportWorkflowStepKey, REPORT_WORKFLOW_STEP_KEYS } from "../utils/reportStatusProgress";
import { toastEmitter } from "../utils/toastEmitter";
import type translations from "../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

const UangJalanReportDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const [report, setReport] = useState<UangJalanReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestRevisionDialogOpen, setRequestRevisionDialogOpen] = useState(false);

  const loadReport = useCallback(
    async (options?: { silent?: boolean }): Promise<UangJalanReport | null> => {
      if (!id) {
        return null;
      }

      const silent = options?.silent ?? false;
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const loadedReport = await getUangJalanReportByIdApi(id);
        setReport(loadedReport);
        return loadedReport;
      } catch (err: any) {
        setError(getErrorMessage(err, t, "fetchUangJalanReport"));
        return null;
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [id, t],
  );

  const refreshReport = useCallback(() => loadReport({ silent: true }), [loadReport]);

  const handleStandbyUpdated = useCallback((updatedOrder: StandbyOrder) => {
    setReport((current) => {
      if (!current?.standbyOrders) {
        return current;
      }
      return {
        ...current,
        standbyOrders: current.standbyOrders.map((order) =>
          order._id === updatedOrder._id ? updatedOrder : order,
        ),
      };
    });
  }, []);

  const handleOrderUpdated = useCallback((updatedOrder: Order) => {
    setReport((current) => {
      if (!current?.orders) {
        return current;
      }

      return {
        ...current,
        orders: current.orders.map((order) =>
          order._id === updatedOrder._id ? updatedOrder : order,
        ),
      };
    });
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const allOrdersVerified = useMemo(() => {
    const members = report ? getReportClaimMembers(report) : [];
    return (
      members.length > 0 &&
      members.every(
        (order) => order.status === OrderStatus.VERIFIED || isOrderApproved(order.status),
      )
    );
  }, [report]);

  const allOrdersApprovedClass = useMemo(() => {
    const members = report ? getReportClaimMembers(report) : [];
    return members.length > 0 && members.every((order) => isOrderApproved(order.status));
  }, [report]);

  const noOrdersInReview = useMemo(() => {
    const members = report ? getReportClaimMembers(report) : [];
    return members.every((order) => order.status !== OrderStatus.IN_REVIEW);
  }, [report]);

  const hasRevisionRequiredOrder = useMemo(() => {
    const members = report ? getReportClaimMembers(report) : [];
    return members.some((order) => order.status === OrderStatus.REVISION_REQUIRED);
  }, [report]);

  const canFinanceAct = !!user && (isFinance(user) || isUserAdmin(user));
  const canApproveAct = !!user && (isFinanceApprover(user) || isUserAdmin(user));
  const userCanMutate = !!user && canMutateOrders(user);
  const reportIsOpen = report?.status === UangJalanReportStatus.OPEN;
  const reportInReview = report?.status === UangJalanReportStatus.IN_REVIEW;
  const reportIsVerified = report?.status === UangJalanReportStatus.VERIFIED;

  const canSubmitForReview = userCanMutate && reportIsOpen;
  const submitForReviewDisabledHint = !userCanMutate
    ? t("permission.operationsOnly" as TranslationKeys)
    : !reportIsOpen
      ? t("reports.submitForReviewDisabledStatus" as TranslationKeys)
      : "";

  const canVerifyReport = canFinanceAct && reportInReview && allOrdersVerified;
  const verifyReportDisabledHint = !canFinanceAct
    ? t("permission.financeOnly" as TranslationKeys)
    : !reportInReview
      ? t("reports.financeActionsDisabledStatus" as TranslationKeys)
      : !allOrdersVerified
        ? t("reports.verifyReportDisabledHint" as TranslationKeys)
        : "";

  const canRequestReportRevision =
    canFinanceAct && reportInReview && noOrdersInReview && hasRevisionRequiredOrder;

  const requestReportRevisionDisabledHint = useMemo(() => {
    if (!canFinanceAct) {
      return t("permission.financeOnly" as TranslationKeys);
    }

    if (!reportInReview) {
      return t("reports.financeActionsDisabledStatus" as TranslationKeys);
    }

    if (canRequestReportRevision) {
      return "";
    }

    const members = report ? getReportClaimMembers(report) : [];
    if (members.some((order) => order.status === OrderStatus.IN_REVIEW)) {
      return t("reports.requestReportRevisionOrdersInReviewHint" as TranslationKeys);
    }

    if (!members.some((order) => order.status === OrderStatus.REVISION_REQUIRED)) {
      return t("reports.requestReportRevisionNoRevisionHint" as TranslationKeys);
    }

    return "";
  }, [canFinanceAct, reportInReview, canRequestReportRevision, report, t]);

  const canApproveReport = canApproveAct && reportIsVerified && allOrdersApprovedClass;
  const approveReportDisabledHint = !canApproveAct
    ? t("permission.approverOnly" as TranslationKeys)
    : !reportIsVerified
      ? t("reports.financeActionsDisabledStatus" as TranslationKeys)
      : !allOrdersApprovedClass
        ? t("reports.approveReportDisabledHint" as TranslationKeys)
        : "";

  const handleSubmitForReview = async () => {
    if (!report) {
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const updatedReport = await submitUangJalanReportForReviewApi(report.readableId);
      setReport(updatedReport);
      toastEmitter.emit(t("reports.submittedForReviewSuccessfully" as TranslationKeys), "success");
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "submitUangJalanReportForReview");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyReport = async () => {
    if (!report) {
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const updatedReport = await verifyUangJalanReportApi(report.readableId);
      setReport(updatedReport);
      toastEmitter.emit(t("reports.verifiedSuccessfully" as TranslationKeys), "success");
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "verifyUangJalanReport");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveReport = async () => {
    if (!report) {
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const updatedReport = await approveUangJalanReportApi(report.readableId);
      setReport(updatedReport);
      toastEmitter.emit(t("reports.approvedSuccessfully" as TranslationKeys), "success");
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "approveUangJalanReport");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!report) {
      return;
    }

    setDownloadingExcel(true);
    setError(null);
    try {
      const { blob, filename } = await downloadUangJalanReportExcelApi(report.readableId, {
        timezone: user?.timezone ?? getClientTimezone(),
      });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err: unknown) {
      const parsedError = await normalizeBlobApiError(err);
      const errorMessage = getErrorMessage(parsedError, t, "fetchUangJalanReport");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setDownloadingExcel(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !report) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  if (!report) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{t("reports.notFound" as TranslationKeys)}</Typography>
      </Paper>
    );
  }

  const workflowSteps = REPORT_WORKFLOW_STEP_KEYS.map((key) => ({
    key,
    label: t(key as TranslationKeys),
  }));

  const submitAction: DetailHeaderAction = {
    id: "submit",
    label: t("reports.submitForReview" as TranslationKeys),
    icon: <SendIcon fontSize="small" />,
    onClick: handleSubmitForReview,
    disabled: actionLoading || !canSubmitForReview,
    disabledHint: submitForReviewDisabledHint,
    testId: TestId.UJR.SUBMIT_FOR_REVIEW,
  };

  const verifyAction: DetailHeaderAction = {
    id: "verify",
    label: t("reports.verifyReport" as TranslationKeys),
    icon: <CheckCircleOutlineIcon fontSize="small" />,
    onClick: handleVerifyReport,
    disabled: actionLoading || !canVerifyReport,
    disabledHint: verifyReportDisabledHint,
    testId: TestId.UJR.VERIFY_REPORT,
  };

  const requestRevisionAction: DetailHeaderAction = {
    id: "request-revision",
    label: t("orders.requestRevision" as TranslationKeys),
    icon: <RateReviewOutlinedIcon fontSize="small" />,
    onClick: () => setRequestRevisionDialogOpen(true),
    disabled: actionLoading || !canRequestReportRevision,
    disabledHint: requestReportRevisionDisabledHint,
    testId: TestId.UJR.REQUEST_REVISION_REPORT_BUTTON,
  };

  const approveAction: DetailHeaderAction = {
    id: "approve",
    label: t("reports.approveReport" as TranslationKeys),
    icon: <TaskAltIcon fontSize="small" />,
    onClick: handleApproveReport,
    disabled: actionLoading || !canApproveReport,
    disabledHint: approveReportDisabledHint,
    testId: TestId.UJR.APPROVE_REPORT,
    hidden: !canApproveAct,
  };

  const transitionActions = [
    submitAction,
    verifyAction,
    requestRevisionAction,
    approveAction,
  ].filter((action) => !action.hidden);
  const enabledTransition = transitionActions.find((action) => !action.disabled);
  const primary =
    enabledTransition ??
    (canApproveAct && reportIsVerified
      ? approveAction
      : canFinanceAct && reportInReview
        ? verifyAction
        : submitAction);

  const menuActions = transitionActions.filter((action) => action.id !== primary.id);

  return (
    <Box data-testid={TestId.UJR.DETAIL} sx={{ pb: 4 }}>
      <DetailPageHeader
        compact
        title={t("reports.detailTitle" as TranslationKeys)}
        subtitle={
          <Box component="span" data-testid={TestId.UJR.READABLE_ID}>
            {report.readableId}
          </Box>
        }
        statusLabel={t("Status" as TranslationKeys)}
        status={report.status}
        statusTestId={TestId.UJR.STATUS}
        metaLine={
          <>
            {t("reports.creationCalendarDate" as TranslationKeys)}: {report.creationCalendarDate}
            {report.teamOperations?.name ? ` · ${report.teamOperations.name}` : ""}
          </>
        }
        progressSlot={
          <StatusWorkflowStepper
            compact
            steps={workflowSteps}
            activeStepKey={getReportWorkflowStepKey(report.status)}
          />
        }
        actions={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
            {canDownloadUangJalanReportExcel(report.status) ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  downloadingExcel ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DownloadOutlinedIcon fontSize="small" />
                  )
                }
                onClick={handleDownloadExcel}
                disabled={actionLoading || downloadingExcel}
                data-testid={TestId.UJR.DOWNLOAD_EXCEL}
                sx={{ textTransform: "none", fontWeight: 600, px: 2, borderRadius: 1.5 }}
              >
                {t("reports.downloadExcel" as TranslationKeys)}
              </Button>
            ) : null}
            <DetailHeaderSplitActions
              primary={primary}
              menuActions={menuActions}
              menuTestId={TestId.UJR.HEADER_ACTIONS_MENU}
              finalState={isReportApproved(report.status)}
            />
          </Box>
        }
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <ReportSummaryCards
        totalPaid={report.totalPaid}
        totalApproved={report.totalApprovedCost}
        difference={report.difference}
        status={report.status}
      />

      <UangJalanReportOrdersSection
        report={report}
        user={user}
        onReportChange={refreshReport}
        onOrderUpdated={handleOrderUpdated}
        onActionError={(message) => {
          setError(message);
          toastEmitter.emit(message, "error");
        }}
      />

      <UangJalanReportStandbySection
        report={report}
        user={user}
        onReportChange={refreshReport}
        onStandbyUpdated={handleStandbyUpdated}
        onActionError={(message) => {
          setError(message);
          toastEmitter.emit(message, "error");
        }}
      />

      <UangJalanReportHistorySection histories={report.histories ?? []} />

      <ReportRequestRevisionDialog
        reportId={report.readableId}
        open={requestRevisionDialogOpen}
        onClose={() => setRequestRevisionDialogOpen(false)}
        onSuccess={async () => {
          await refreshReport();
        }}
      />
    </Box>
  );
};

export default UangJalanReportDetailPage;
